/**
 * Shared logic with DiscountTable: material/labor subtotals and per-definition tax lines.
 */

export function accumulateLineItemSubtotals(lineItems) {
  if (!lineItems?.length) {
    return {
      hasMaterials: false,
      hasLabor: false,
      materialSubTotal: 0,
      laborSubTotal: 0,
      taxableMaterialSubTotal: 0,
      taxableLaborSubTotal: 0,
    };
  }

  return lineItems.reduce(
    (acc, item) => {
      if (!item?.Name?.trim()) return acc;

      const total = Number(item?.Total || 0);
      const isNonTaxable =
        item?.IsNonTaxable === true || item?.IsNonTaxable === "true" || item?.IsNonTaxable === 1;

      if (item?.Type?.toLowerCase() === "materials" || item?.Type?.toLowerCase() === "material") {
        acc.hasMaterials = true;
        acc.materialSubTotal += total;
        if (!isNonTaxable) acc.taxableMaterialSubTotal += total;
      } else if (item?.Type?.toLowerCase() === "labor") {
        acc.hasLabor = true;
        acc.laborSubTotal += total;
        if (!isNonTaxable) acc.taxableLaborSubTotal += total;
      } else {
        acc.hasLabor = true;
        acc.laborSubTotal += total;
        if (!isNonTaxable) acc.taxableLaborSubTotal += total;
      }
      return acc;
    },
    {
      hasMaterials: false,
      hasLabor: false,
      materialSubTotal: 0,
      laborSubTotal: 0,
      taxableMaterialSubTotal: 0,
      taxableLaborSubTotal: 0,
    }
  );
}

/**
 * @param {Array} taxes - company tax definitions from GET /v1/taxes
 * @param {ReturnType<typeof accumulateLineItemSubtotals>} subtotals
 */
export function computeTaxDetailsFromDefinitions(taxes, subtotals) {
  const {
    hasMaterials,
    hasLabor,
    taxableMaterialSubTotal,
    taxableLaborSubTotal,
  } = subtotals || {};

  if (!taxes?.length) {
    return {
      totalMaterialTaxes: 0,
      totalLaborTaxes: 0,
      materialTaxDetails: [],
      laborTaxDetails: [],
    };
  }

  const result = {
    totalMaterialTaxes: 0,
    totalLaborTaxes: 0,
    materialTaxDetails: [],
    laborTaxDetails: [],
  };

  taxes.forEach((tax) => {
    const types = Array.isArray(tax.types) ? tax.types : [tax.type];

    if (
      hasMaterials &&
      types.some((type) => type?.toLowerCase?.() === "material" || type?.toLowerCase?.() === "materials")
    ) {
      const feeType = tax.feeType || "Percentage";
      const value = feeType === "Percentage" ? tax.percentage || 0 : tax.feeAmount || 0;
      let amount = 0;
      if (feeType === "Percentage") {
        amount = (taxableMaterialSubTotal * value) / 100;
      } else {
        amount = taxableMaterialSubTotal > 0 ? value : 0;
      }
      if (amount > 0) {
        result.totalMaterialTaxes += amount;
        result.materialTaxDetails.push({
          id: tax.id,
          name: tax.name || "",
          percentage: feeType === "Percentage" ? value : 0,
          feeType,
          feeAmount: feeType === "Fixed" ? value : 0,
          type: "Materials",
          amount,
        });
      }
    }

    if (hasLabor && types.some((type) => type?.toLowerCase?.() === "labor")) {
      const feeType = tax.feeType || "Percentage";
      const value = feeType === "Percentage" ? tax.percentage || 0 : tax.feeAmount || 0;
      let amount = 0;
      if (feeType === "Percentage") {
        amount = (taxableLaborSubTotal * value) / 100;
      } else {
        amount = taxableLaborSubTotal > 0 ? value : 0;
      }
      if (amount > 0) {
        result.totalLaborTaxes += amount;
        result.laborTaxDetails.push({
          id: tax.id,
          name: tax.name || "",
          percentage: feeType === "Percentage" ? value : 0,
          feeType,
          feeAmount: feeType === "Fixed" ? value : 0,
          type: "Labor",
          amount,
        });
      }
    }
  });

  return result;
}

export function mergeTaxDetailRows(materialTaxDetails, laborTaxDetails) {
  return [...(materialTaxDetails || []), ...(laborTaxDetails || [])];
}
